interface CanonicalQueryResponse {
  canonical: string;
  is_new: boolean;
  frequency: number;
  similar_queries: string[];
}

interface QuerySuggestion {
  query: string;
  frequency: number;
}

interface ClusterStats {
  total_clusters: number;
  total_queries: number;
  avg_queries_per_cluster: number;
  deduplication_rate: number;
  similarity_threshold: number;
}

class QuerySpaceService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_FASTAPI_URL || 'http://localhost:8000';
  }

  async getCanonicalQuery(query: string): Promise<CanonicalQueryResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/query/canonical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Query deduplication:', {
        original: query,
        canonical: data.canonical,
        is_new: data.is_new,
        frequency: data.frequency
      });

      return data;
    } catch (error) {
      console.error('Error getting canonical query:', error);
      // Fallback: return original query if service fails
      return {
        canonical: query,
        is_new: true,
        frequency: 1,
        similar_queries: []
      };
    }
  }

  async getQuerySuggestions(partial: string, limit: number = 5): Promise<QuerySuggestion[]> {
    try {
      if (partial.length < 2) {
        return [];
      }

      const response = await fetch(`${this.baseUrl}/api/query/suggestions?q=${encodeURIComponent(partial)}&limit=${limit}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting query suggestions:', error);
      return [];
    }
  }

  async getClusterStats(): Promise<ClusterStats | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/query/stats`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting cluster stats:', error);
      return null;
    }
  }

  async initializeQuerySpace(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/query/initialize`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Query Space initialized:', data);
      return true;
    } catch (error) {
      console.error('Error initializing query space:', error);
      return false;
    }
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/query/health`);
      const data = await response.json();
      
      console.log('Query Space health:', data);
      return data.status === 'healthy';
    } catch (error) {
      console.error('Query Space health check failed:', error);
      return false;
    }
  }
}

export const querySpaceService = new QuerySpaceService();
export type { CanonicalQueryResponse, QuerySuggestion, ClusterStats };